// Angular
import { Inject, Injectable } from '@angular/core';

// Third party
import { Capacitor } from '@capacitor/core';
import { CookieService } from 'ngx-cookie-service';
import moment from 'moment';
import { DurationInputArg1, DurationInputArg2 } from 'moment';

// Local library
import { ILoggedInUserModel, Web, ipPattern } from '@amwager/util-ts';

// Local file
import { DEFAULT_ROLE_ID, DOMAIN, UI } from '../tokens';


/**
 * Manages user session data through cookies and local storage
 */
@Injectable()
export class SessionService {

  private readonly windowHostname =  window.location.hostname;
  private readonly localStorage =  window.localStorage;
  private readonly platform = Capacitor.getPlatform();

  // Safari (unlike Chrome) drops `Secure` cookies over http, so only mark
  // secure on https. Native keeps prior behavior (ios: false, android: true).
  private readonly secureCookie: boolean =
    this.platform === 'ios'
      ? false
      : this.platform === 'android'
        ? true
        : window.location.protocol === 'https:';

  constructor(
    @Inject(DEFAULT_ROLE_ID) private readonly defaultRoleId: string,
    @Inject(UI) private readonly appUi: string,
    @Inject(DOMAIN) private readonly domain: string,
    private readonly cookieService: CookieService,
  ) {}

  /**
   * Compute an expiration Date offset from now
   * @param {number} days Duration amount to add to now
   * @param {string} interval Duration unit (days, months, years...)
   * @returns {Date} Computed expiration date
   */
  getExpiresDate(days: number = 1, interval: string = 'days'): Date {
    const cookieMoment: moment.Moment = moment(4, "HH");

    return new Date(cookieMoment.add(
      days as DurationInputArg1,
      interval as DurationInputArg2
    ).toDate());
  }

  /**
   * Build cookie options with the current domain and expiration
   * @param {string} path Cookie path
   * @param {number} expDate Number of intervals until expiration
   * @param {string} expInterval Expiration interval unit
   * @returns {Web.ICookie} Cookie configuration
   */
  getOptions(path: string = '/', expDate?: number, expInterval?: string): Web.ICookie {
    return {
      path,
      domain: this.getCookieDomain(),
      expires: expDate ? this.getExpiresDate(expDate, expInterval) : expDate
    }
  }

  /**
   * Persist login response fields as cookies with tailored expirations
   * @param {Partial<ILoggedInUserModel>} data Login response payload
   * @param {string} email User email address
   * @returns {void}
   */
  storeCookiesLoginResponse(
      data: Partial<ILoggedInUserModel>, email: string
    ): void {

    this.resetLogout();

    const options1day: Web.ICookie = this.getOptions('/', 1);
    const options7days: Web.ICookie = this.getOptions('/', 7);
    const options20yrs: Web.ICookie = this.getOptions('/', 20, 'y');

    // options 1 day
    if (data.tUserId) this.setTUserId(data.tUserId, options1day);
    if (data.Token) this.setUserToken(data.Token, options1day);
    if (data.TPToken) this.setTPToken(data.TPToken, options1day);
    if (data.FirstName) this.setUserFName(data.FirstName, options1day);
    if (data.LastName) this.setUserLast(data.LastName, options1day);
    if (data.DOB) this.setUserDOB(data.DOB, options1day);
    if (data.Subscription) this.setSubscription(data.Subscription, options1day);
    if (data.ReferalId) this.setReferralId(data.ReferalId, options1day);
    if (data.PaypageId) this.setPayPageId(data.PaypageId, options1day);
    if (data.Affiliate?.AffiliateName) this.setAffiliateName(
      data.Affiliate.AffiliateName,
      options1day
    );
    this.setRoleId(data.RoleId || this.defaultRoleId, options1day);

    if (data.State) this.setState(data.State, options1day);
    if (data.Country) this.setCountry(data.Country, options1day);
    if (data.Language) this.setLanguage(data.Language, options1day);

    // options 7 days
    if (email) this.setUserEmail(email, options7days);

    // options 20 years
    if (data.LastLoginTime) this.setLastLogin(data.LastLoginTime, options20yrs);
    if (this.appUi) this.setLastUI(this.appUi, options20yrs);
  }

  /**
   * Pick the cookie domain for the current hostname (IP-safe)
   * @returns {string} Domain string to use for cookies
   */
  getCookieDomain(): string {
    const ipValidator = ipPattern;
    let cookieDomain: string = this.domain;

    if (ipValidator.test(this.windowHostname)) cookieDomain = this.windowHostname;
    else {
      if (this.windowHostname.split(".").length - 1 > 1) cookieDomain = this.domain;
      else cookieDomain = this.windowHostname;
    }

    return cookieDomain || '';
  }

  /**
   * Generate a UUID-v4-like session identifier
   * @returns {string} Unique session identifier
   */
  generateSessionId(): string {
    const nValues = [8,9,'a','b'];

    //  M = 1-5, N = [8,9,'a','b'],  derived from uuidv4
    const sessionId = 'xxxxxxxx-xxxx-Mxxx-Nxxx-xxxxxxxxxxxx'.replace(
      /[xyNM]/g,
      (c: string): string => {
        if ( c == 'M' ) return String(
          (Math.floor(Math.random() * 5) + 1).toString()
        );
        else if ( c == 'N' ) return String(
          nValues[ Math.floor(Math.random() * 4) ]
        );

        const r = Math.random()*16|0, v = c === 'x' ? r : (r&0x3|0x8);
        return v.toString(16);
    });

    return sessionId;
  }

  /**
   * Clear session cookies/localStorage (keeps remember-me email)
   * @returns {void}
   */
  resetLogout(): void {
    const options7days = this.getOptions('/', 7);
    const options20yrs: Web.ICookie = this.getOptions('/', 20, 'y');

    const isRememberMe: boolean = this.getIsRememberMe();
    const lastlogin: string = this.getLastLogin();

    let tempEmail = '';

    // get email before deleting all cookies
    if (isRememberMe) tempEmail = this.getIsRememberEmail();

    this.cookieService.deleteAll('/', options7days.domain);
    this.localStorage.clear();

    if (isRememberMe && tempEmail) {
      this.setUserEmail(tempEmail, options7days);

      if (tempEmail !== 'Guest')
        this.setIsRememberEmail(tempEmail, options20yrs);

      this.setIsRememberMe(String(isRememberMe), options20yrs);
    }

    this.setLastLogin(lastlogin, options20yrs);
    this.setLastUI(this.appUi, options20yrs);
  }

  /**
   * Get the Trulioo user id
   * @returns {string} Trulioo user id or ''
   */
  getTUserId(): string {
    return this.cookieService.get("tUserId") || '';
  }

  /**
   * Set the Trulioo user id cookie
   * @param {string} tUserId Trulioo user id
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setTUserId(tUserId: string, options: Web.ICookie): void {
    this.delTUserId(options);

    this.cookieService.set(
      "tUserId",
      tUserId,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the Trulioo user id cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delTUserId(options: Web.ICookie): void {
    this.cookieService.delete("tUserId", options.path, options.domain);
  }

  /**
   * True when a non-empty auth token is present
   * @returns {boolean} True when token is valid
   */
  hasToken(): boolean {
    const token = this.getUserToken();
    return (token !== null) && (token !== 'null') && (token !== undefined)
      && (token !== 'undefined') && (token !== '');
  }

  /**
   * Get the user auth token
   * @returns {string} User token or ''
   */
  getUserToken(): string {
    return this.cookieService.get("userToken") || '';
  }

  /**
   * Set the user auth token cookie
   * @param {string} token Auth token
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setUserToken(token: string, options: Web.ICookie): void {
    this.delUserToken(options);

    this.cookieService.set(
      "userToken",
      token,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user auth token cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delUserToken(options: Web.ICookie): void {
    this.cookieService.delete("userToken", options.path, options.domain);
  }

  /**
   * Get the third-party token
   * @returns {string} TP token or ''
   */
  getTPToken(): string {
    return this.cookieService.get('TpToken') || '';
  }

  /**
   * Set the third-party token cookie
   * @param {string} tptoken Third-party token
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setTPToken(tptoken: string, options: Web.ICookie): void {
    this.delTPToken(options);

    this.cookieService.set(
      "TpToken",
      tptoken,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the third-party token cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delTPToken(options: Web.ICookie): void {
    this.cookieService.delete("TpToken", options.path, options.domain);
  }

  /**
   * Get the user's first name
   * @returns {string} First name or ''
   */
  getUserFName(): string {
    return this.cookieService.get("userFirst") || '';
  }

  /**
   * Set the user's first name cookie
   * @param {string} fname First name
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setUserFName(fname: string, options: Web.ICookie): void {
    this.delUserFName(options);

    this.cookieService.set(
      "userFirst",
      fname,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's first name cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delUserFName(options: Web.ICookie): void {
    this.cookieService.delete("userFirst", options.path, options.domain);
  }

  /**
   * Get the user's last name
   * @returns {string} Last name or ''
   */
  getUserLast(): string {
    return this.cookieService.get("userLast") || '';
  }

  /**
   * Set the user's last name cookie
   * @param {string} lastName Last name
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setUserLast(lastName: string, options: Web.ICookie): void {
    this.delUserLast(options);

    this.cookieService.set(
      "userLast",
      lastName,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's last name cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delUserLast(options: Web.ICookie): void {
    this.cookieService.delete("userLast", options.path, options.domain);
  }

  /**
   * Get the user's date of birth
   * @returns {string} DOB or ''
   */
  getUserDOB(): string {
    return this.cookieService.get("userDOB") || '';
  }

  /**
   * Set the user's date of birth cookie
   * @param {string} userDOB Date of birth
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setUserDOB(userDOB: string, options: Web.ICookie): void {
    this.delUserDOB(options);

    this.cookieService.set(
      "userDOB",
      userDOB,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's date of birth cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delUserDOB(options: Web.ICookie): void {
    this.cookieService.delete("userDOB", options.path, options.domain);
  }

  /**
   * Get the user's email address
   * @returns {string} Email or ''
   */
  getUserEmail(): string {
    return this.cookieService.get("userEmail") || '';
  }

  /**
   * Set the user's email cookie
   * @param {string} email Email address
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setUserEmail(email: string, options: Web.ICookie): void {
    this.delUserEmail(options);

    this.cookieService.set(
      "userEmail",
      email,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's email cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delUserEmail(options: Web.ICookie): void {
    this.cookieService.delete("userEmail", options.path, options.domain);
  }

  /**
   * Get the user's subscription
   * @returns {string} Subscription or ''
   */
  getSubscription(): string {
    return this.cookieService.get("Subscription") || '';
  }

  /**
   * Set the user's subscription cookie
   * @param {string} subscription Subscription value
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setSubscription(subscription: string, options: Web.ICookie): void {
    this.delSubscription(options);

    this.cookieService.set(
      "Subscription",
      subscription,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's subscription cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delSubscription(options: Web.ICookie): void {
    this.cookieService.delete("Subscription", options.path, options.domain);
  }

  /**
   * Get the affiliate name
   * @returns {string} Affiliate name or ''
   */
  getAffiliateName(): string {
    return this.cookieService.get("AffiliateName") || '';
  }

  /**
   * Set the affiliate name cookie
   * @param {string} affiliateName Affiliate name
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setAffiliateName(affiliateName: string, options: Web.ICookie): void {
    this.delAffiliateName(options);

    this.cookieService.set(
      "AffiliateName",
      affiliateName,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the affiliate name cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delAffiliateName(options: Web.ICookie): void {
    this.cookieService.delete("AffiliateName", options.path, options.domain);
  }

  /**
   * Get the last login timestamp
   * @returns {string} Last login or ''
   */
  getLastLogin(): string {
    return this.cookieService.get("lastLogin") || '';
  }

  /**
   * Set the last login timestamp cookie
   * @param {string} lastLogin Last login timestamp
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setLastLogin(lastLogin: string, options: Web.ICookie): void {
    this.delLastLogin(options);

    this.cookieService.set(
      "lastLogin",
      lastLogin,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the last login timestamp cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delLastLogin(options: Web.ICookie): void {
    this.cookieService.delete("lastLogin", options.path, options.domain);
  }

  /**
   * Get the referral id
   * @returns {string} Referral id or ''
   */
  getReferralId(): string {
    return this.cookieService.get("ReferralId") || '';
  }

  /**
   * Set the referral id cookie
   * @param {string} referralId Referral id
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setReferralId(referralId: string, options: Web.ICookie): void {
    this.delReferralId(options);

    this.cookieService.set(
      "ReferralId",
      referralId,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the referral id cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delReferralId(options: Web.ICookie): void {
    this.cookieService.delete("ReferralId", options.path, options.domain);
  }

  /**
   * Get the user's role id (defaults to the injected default)
   * @returns {number} Role id
   */
  getRoleId(): number {
    return Number(this.cookieService.get("roleID") || this.defaultRoleId);
  }

  /**
   * Set the user's role id cookie
   * @param {string | number} roleId Role id
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setRoleId(roleId: string | number, options: Web.ICookie): void {
    this.delRoleId(options);

    this.cookieService.set(
      "roleID",
      String(roleId),
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's role id cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delRoleId(options: Web.ICookie): void {
    this.cookieService.delete("roleID", options.path, options.domain);
  }

  /**
   * Get the EProtect Credit Card pay page id
   * @returns {string} Pay page id or ''
   */
  getPayPageId(): string {
    return this.cookieService.get("payPageId") || '';
  }

  /**
   * Set the pay page id cookie
   * @param {string} payPageId Pay page id
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setPayPageId(payPageId: string, options: Web.ICookie): void {
    this.delPayPageId(options);

    this.cookieService.set(
      "payPageId",
      payPageId || '',
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the pay page id cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delPayPageId(options: Web.ICookie): void {
    this.cookieService.delete("payPageId", options.path, options.domain);
  }

  /**
   * Get the last UI identifier
   * @returns {string} Last UI or ''
   */
  getLastUi(): string {
    return this.cookieService.get("lastUI") || '';
  }

  /**
   * Set the last UI identifier cookie
   * @param {string} lastUI UI identifier (defaults to current app UI)
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setLastUI(lastUI: string = this.appUi, options: Web.ICookie): void {
    this.delLastUI(options);

    this.cookieService.set(
      "lastUI",
      lastUI,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the last UI identifier cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delLastUI(options: Web.ICookie): void {
    this.cookieService.delete("lastUI", options.path, options.domain);
  }

  /**
   * True when remember-me is enabled
   * @returns {boolean} Remember-me flag
   */
  getIsRememberMe(): boolean {
    return this.cookieService.get("isRememberMe") == 'true' ? true : false;
  }

  /**
   * Set the remember-me preference cookie
   * @param {string} isRememberMe 'true' or 'false'
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setIsRememberMe(isRememberMe: string, options: Web.ICookie): void {
    this.cookieService.set(
      "isRememberMe",
      isRememberMe,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the remember-me preference cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delIsRememberMe(options: Web.ICookie): void {
    this.cookieService.delete("isRememberMe", options.path, options.domain);
  }

  /**
   * Get the remember-me email (kept separate from GUEST email)
   * @returns {string} Remembered email or ''
   */
  getIsRememberEmail(): string {
    return this.cookieService.get("isRememberEmail") || '';
  }

  /**
   * Set the remember-me email cookie
   * @param {string} email Email to remember
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setIsRememberEmail(email: string, options: Web.ICookie): void {
    this.cookieService.set(
      "isRememberEmail",
      email,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the remember-me email cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delIsRememberEmail(options: Web.ICookie): void {
    this.cookieService.delete("isRememberEmail", options.path, options.domain);
  }

  /**
   * Get the previous URL
   * @returns {string} Previous URL or ''
   */
  getPrevUrl(): string {
    return this.cookieService.get("prevUrl") || '';
  }

  /**
   * Set the previous URL cookie (1-day expiration)
   * @param {string} prevUrl Previous URL
   * @returns {void}
   */
  setPrevUrl(prevUrl: string): void {
    const options1day: Web.ICookie = this.getOptions('/', 1);
    this.cookieService.set(
      "prevUrl",
      prevUrl,
      options1day.expires,
      options1day.path,
      options1day.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the previous URL cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delPrevUrl(options: Web.ICookie): void {
    this.cookieService.delete("prevUrl", options.path, options.domain);
  }

  /**
   * Get the current ticket
   * @returns {any} Ticket data or {}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getTicket(): any {
    return this.cookieService.get("currentTicket") || {};
  }

  /**
   * Set the current ticket cookie
   * @param {any} data Ticket data
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setTicket(data: any, options: Web.ICookie): void {
    this.delTicket(options);

    this.cookieService.set(
      "currentTicket",
      data,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the current ticket cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delTicket(options: Web.ICookie): void {
    this.cookieService.delete("currentTicket", options.path, options.domain);
  }

  /**
   * True when change-password flow is active
   * @returns {any} Change-password active flag
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getChangePasswordIsActive(): any {
    return this.cookieService.get("changePasswordIsActive") == 'true' ? true : false;
  }

  /**
   * Set the change-password active cookie
   * @param {any} data Active flag value
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setChangePasswordIsActive(data: any, options: Web.ICookie): void {
    this.delChangePasswordIsActive(options);

    this.cookieService.set(
      "changePasswordIsActive",
      data,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the change-password active cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delChangePasswordIsActive(options: Web.ICookie): void {
    this.cookieService.delete("changePasswordIsActive", options.path, options.domain);
  }

  /**
   * Get the user's state/province
   * @returns {string} State or ''
   */
  getState(): string {
    return this.cookieService.get("state") || '';
  }

  /**
   * Set the user's state/province cookie
   * @param {string} fname State/province
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setState(fname: string, options: Web.ICookie): void {
    this.delState(options);
    this.cookieService.set(
      "state",
      fname,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's state/province cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delState(options: Web.ICookie): void {
    this.cookieService.delete("state", options.path, options.domain);
  }

  /**
   * Get the user's country
   * @returns {string} Country or ''
   */
  getCountry(): string {
    return this.cookieService.get("country") || '';
  }

  /**
   * Set the user's country cookie
   * @param {string} fname Country code
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setCountry(fname: string, options: Web.ICookie): void {
    this.delCountry(options);

    this.cookieService.set(
      "country",
      fname,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's country cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delCountry(options: Web.ICookie): void {
    this.cookieService.delete("country", options.path, options.domain);
  }

  /**
   * Get the user's UI language code
   * @returns {string} Language code (e.g. 'en') or ''
   */
  getLanguage(): string {
    return this.cookieService.get("Language") || '';
  }

  /**
   * Set the user's UI language cookie
   * @param {string} language Language code (e.g. 'en')
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  setLanguage(language: string, options: Web.ICookie): void {
    this.delLanguage(options);

    this.cookieService.set(
      "Language",
      language,
      options.expires,
      options.path,
      this.platform === 'android' ? '' : options.domain,
      this.secureCookie
    );
  }

  /**
   * Delete the user's UI language cookie
   * @param {Web.ICookie} options Cookie options
   * @returns {void}
   */
  delLanguage(options: Web.ICookie): void {
    this.cookieService.delete("Language", options.path, options.domain);
  }

}
